import {
  Component,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  input,
} from '@angular/core';
import 'chartjs-adapter-dayjs-3';
import { ChartConfiguration, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective, NgChartsModule } from 'ng2-charts';
import { PivotTableHeader } from '../../raw-pivot-table/raw-pivot-table.component';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

@Component({
  selector: 'ag-pivot-chart',
  templateUrl: './pivot-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgChartsModule],
})
export class PivotChartComponent implements OnChanges {
  readonly rawDataHash = input({});

  readonly rows = input<PivotTableHeader[]>([]);

  readonly cols = input<PivotTableHeader[]>([]);

  @ViewChild(BaseChartDirective, { static: false }) chart?: BaseChartDirective;

  readonly chartType = input<ChartType>('bar');

  chartData: ChartConfiguration<'line', { x: string; y: number }[]>['data'] = {
    datasets: [],
  };

  regularChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  // when using a line graph with dates, we can use a time scale to ensure dates follow sequentially
  // this is a chartjs feature where the x-axis ticks are calculated automatically, and then your data plotted in between
  timeSeriesOptions: ChartOptions = {
    ...this.regularChartOptions,
    scales: {
      x: {
        type: 'time',
        display: true,
        time: {
          unit: 'week',
          displayFormats: {
            week: 'GGGG[-W]WW',
          },
          isoWeekday: true,
        },
        ticks: {
          source: 'auto',
        },
      },
    },
  };

  chartOptions = this.regularChartOptions;
  chartLegend = true;

  constructor() {}

  ngOnChanges(changes: SimpleChanges): void {
    this.updateChart();
  }

  // convert the aggregated hash into a format usable by ngCharts
  updateChart() {
    const datasets = [];
    const dataset = {
      label: '',
      data: [],
    };
    if (this.rows().length == 0) {
      datasets.push(dataset);
    }

    Object.keys(this.rawDataHash())
      .sort()
      .forEach((i) => {
        this.addToDataSet(this.rawDataHash()[i], i, datasets, dataset, 0);
      });

    this.chartData.datasets = datasets;

    this.configureChart();

    this.chart?.update();
  }

  // core logic behind the below recursive function is that any "rows" become datasets
  // any cols become datapoints (x, y), where x is the col heading and y is the value in the table
  addToDataSet(currHash, prefix, datasets, dataset, depth = 0) {
    if (depth >= this.rows().length) {
      this.addDataPoint(currHash, prefix, dataset);
    } else {
      dataset = { label: prefix, data: [] };
      datasets.push(dataset);
      if (!currHash.children) {
        this.addDataPoint(currHash, '', dataset);
      } else {
        Object.keys(currHash.children)
          .sort()
          .forEach((child) => {
            const finalPrefix =
              depth + 1 >= this.rows().length ? child : `${prefix} - ${child}`;
            this.addToDataSet(
              currHash.children[child],
              finalPrefix,
              datasets,
              dataset,
              depth + 1,
            );
          });
      }
    }
  }

  addDataPoint(currHash, prefix: string, dataset) {
    if (!currHash.children) {
      const firstKey = Object.keys(currHash.values)[0];

      const match = prefix.match(/(\d{4,})-W(\d{1,})/);

      let xLabel = prefix;

      if (match) {
        const [_entireMatch, year, week] = match;

        xLabel = dayjs()
          .year(Number(year))
          .isoWeek(Number(week))
          .day(1)
          .startOf('date')
          .toISOString();
      }

      dataset.data.push({
        x: xLabel,
        y: currHash.values[firstKey],
      });
    } else {
      Object.keys(currHash.children)
        .sort()
        .forEach((child) => {
          this.addDataPoint(
            currHash.children[child],
            `${prefix} - ${child}`,
            dataset,
          );
        });
    }
  }

  configureChart() {
    const cols = this.cols();
    if (
      cols?.length == 1 &&
      this.chartType() == 'line' &&
      (cols[0].field.toLowerCase().includes('week') ||
        cols[0].field.toLowerCase().includes('date'))
    ) {
      this.chartOptions = this.timeSeriesOptions;
      this.chartData.labels = [];
    } else {
      this.chartOptions = this.regularChartOptions;
      this.chartData.labels = [];
    }
  }
}
